import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Loader2, ChevronDown, ChevronRight, CheckCircle2, Circle,
  Briefcase, Calendar, ArrowLeft, Receipt, Plus,
} from 'lucide-react';
import { toast } from 'sonner';
import { StaffBottomNav, StaffHeader } from '@/components/staff/StaffNav';
import { mePayPeriods, fmtErr } from '@/lib/staffApi';
import { LogHoursDialog } from '@/pages/staff/StaffPages';

/* ── Date helpers (purely string-based to avoid tz drift) ─────────── */
function periodLabel(start, end) {
  const fmt = (s) => {
    if (!s) return '';
    const [y, m, d] = s.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  };
  const [endY] = (end || start || '').split('-');
  return `${fmt(start)} – ${fmt(end)}, ${endY}`;
}
function dayLabel(s) {
  if (!s) return '';
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
}

function StatusBadge({ paid, isClosed }) {
  if (paid) {
    return <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 hover:bg-emerald-100 gap-1"><CheckCircle2 className="h-3 w-3" />Paid</Badge>;
  }
  if (isClosed) {
    return <Badge className="bg-amber-100 text-amber-800 border-amber-200 hover:bg-amber-100 gap-1"><Circle className="h-3 w-3" />Closed · Unpaid</Badge>;
  }
  return <Badge variant="outline">Open</Badge>;
}

export function StaffPayPeriodsPage() {
  const [data, setData] = useState(null);
  const [showLog, setShowLog] = useState(false);

  const refresh = () => {
    mePayPeriods().then(setData).catch(e => toast.error(fmtErr(e, 'Failed to load pay periods')));
  };
  useEffect(refresh, []);

  const { current, past } = useMemo(() => {
    if (!data) return { current: null, past: [] };
    const today = new Date().toISOString().slice(0, 10);
    const cur = data.items.find(it => it.period_start === data.current_period_start)
      || { period_start: data.current_period_start, period_end: data.current_period_end, total_hours: 0, entries: [], by_project: [], by_day: [], meta: null };
    const past = data.items.filter(it => it.period_start !== data.current_period_start);
    cur.__open = cur.period_end >= today;
    return { current: cur, past };
  }, [data]);

  return (
    <div className="min-h-screen bg-[#fbfaf7]" data-testid="staff-pay-periods-page">
      <StaffHeader
        title="Pay periods"
        action={
          <Button size="sm" className="bg-emerald-700 hover:bg-emerald-800 text-white" onClick={() => setShowLog(true)} data-testid="staff-hours-log-btn">
            <Plus className="h-3.5 w-3.5 mr-1" />Log
          </Button>
        }
      />
      <main className="max-w-2xl mx-auto px-4 py-5 pb-24 space-y-6">
        <Link to="/staff/home" className="text-xs text-muted-foreground hover:text-[#1f2933] inline-flex items-center gap-1" data-testid="pp-back-home">
          <ArrowLeft className="h-3 w-3" /> Home
        </Link>

        {!data ? (
          <div className="py-16 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-emerald-600" /></div>
        ) : (
          <>
            {/* Current period */}
            <section data-testid="pp-current-section">
              <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2 font-medium">Current pay period</p>
              <CurrentPeriodCard period={current} />
            </section>

            {/* Past */}
            <section data-testid="pp-past-section">
              <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2 font-medium">Past periods ({past.length})</p>
              {past.length === 0 ? (
                <p className="text-sm text-muted-foreground italic">No past pay periods yet.</p>
              ) : (
                <div className="space-y-2">
                  {past.map(p => <PastPeriodRow key={p.period_start} period={p} />)}
                </div>
              )}
            </section>
          </>
        )}
      </main>
      <StaffBottomNav />
      <LogHoursDialog open={showLog} onOpenChange={setShowLog} onLogged={refresh} />
    </div>
  );
}

function CurrentPeriodCard({ period }) {
  const isClosed = !period.__open;
  const paid = !!period.meta?.paid;
  return (
    <Card className="border-emerald-200" data-testid="pp-current-card">
      <CardContent className="p-4 space-y-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <p className="text-sm font-semibold text-[#1f2933]">{periodLabel(period.period_start, period.period_end)}</p>
            <p className="text-[11px] text-muted-foreground mt-0.5">
              {period.entries.length} session{period.entries.length === 1 ? '' : 's'} · {period.by_project.length} project{period.by_project.length === 1 ? '' : 's'}
            </p>
          </div>
          <StatusBadge paid={paid} isClosed={isClosed} />
        </div>

        <div className="flex items-baseline gap-2">
          <p className="text-3xl font-bold tabular-nums text-emerald-700" data-testid="pp-current-total">
            {period.total_hours.toFixed(2)}
          </p>
          <p className="text-sm text-muted-foreground">hrs logged</p>
        </div>

        {period.entries.length === 0 ? (
          <p className="text-sm text-muted-foreground italic">No hours logged yet this period.</p>
        ) : (
          <>
            <ByProjectList rows={period.by_project} />
            <ByDayList rows={period.by_day} />
          </>
        )}

        {period.meta && <PayMetaDisplay meta={period.meta} />}
      </CardContent>
    </Card>
  );
}

function PastPeriodRow({ period }) {
  const [open, setOpen] = useState(false);
  const paid = !!period.meta?.paid;
  return (
    <Card data-testid={`pp-past-${period.period_start}`}>
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center gap-2 px-3 py-3 text-left hover:bg-muted/30 transition-colors"
      >
        {open ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium truncate">{periodLabel(period.period_start, period.period_end)}</p>
          <p className="text-[11px] text-muted-foreground">
            {period.entries.length} session{period.entries.length === 1 ? '' : 's'} · {period.by_project.length} project{period.by_project.length === 1 ? '' : 's'}
          </p>
        </div>
        <StatusBadge paid={paid} isClosed />
        <span className="text-sm font-semibold tabular-nums w-16 text-right">{period.total_hours.toFixed(2)} hr</span>
      </button>

      {open && (
        <CardContent className="border-t bg-muted/10 p-4 space-y-4">
          {period.entries.length > 0 && (
            <>
              <ByProjectList rows={period.by_project} />
              <ByDayList rows={period.by_day} />
            </>
          )}
          {period.meta ? <PayMetaDisplay meta={period.meta} /> : (
            <p className="text-xs text-muted-foreground italic">Payment hasn't been recorded for this period yet.</p>
          )}
        </CardContent>
      )}
    </Card>
  );
}

function ByProjectList({ rows }) {
  if (!rows || rows.length === 0) return null;
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium mb-1.5 flex items-center gap-1.5">
        <Briefcase className="h-3 w-3" /> By project
      </p>
      <div className="space-y-1">
        {rows.map(r => (
          <div key={r.project_id} className="flex items-center gap-2 text-sm" data-testid={`pp-by-project-${r.project_id}`}>
            <span className="flex-1 truncate">{r.project_name}</span>
            <span className="text-[11px] text-muted-foreground tabular-nums">{r.entries}×</span>
            <span className="tabular-nums font-medium w-20 text-right">{r.hours.toFixed(2)} hr</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ByDayList({ rows }) {
  if (!rows || rows.length === 0) return null;
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium mb-1.5 flex items-center gap-1.5">
        <Calendar className="h-3 w-3" /> By day
      </p>
      <div className="space-y-1">
        {rows.map(r => (
          <div key={r.date} className="flex items-center gap-2 text-sm" data-testid={`pp-by-day-${r.date}`}>
            <span className="flex-1 truncate">{dayLabel(r.date)}</span>
            <span className="tabular-nums font-medium w-20 text-right">{r.hours.toFixed(2)} hr</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function PayMetaDisplay({ meta }) {
  const fmtMoney = (n) => (n == null ? '—' : `$${Math.round(Number(n)).toLocaleString('en-US')}`);
  return (
    <div className="rounded-md border bg-white p-3 space-y-2" data-testid="pp-pay-meta">
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold text-[#1f2933] flex items-center gap-1.5">
          <Receipt className="h-3.5 w-3.5 text-emerald-600" /> Payment
        </p>
        {meta.paid
          ? <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 hover:bg-emerald-100 gap-1"><CheckCircle2 className="h-3 w-3" />Paid</Badge>
          : <Badge variant="outline" className="text-amber-700 border-amber-200">Pending</Badge>}
      </div>
      <div className="grid grid-cols-2 gap-2 text-xs">
        <MetaRow label="Amount" value={fmtMoney(meta.amount)} />
        <MetaRow label="Pay date" value={meta.pay_date || '—'} />
        <MetaRow label="Method" value={meta.method || '—'} />
      </div>
      {meta.notes && (
        <div className="text-xs">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Notes</p>
          <p className="text-[#1f2933] whitespace-pre-wrap mt-0.5">{meta.notes}</p>
        </div>
      )}
    </div>
  );
}

function MetaRow({ label, value }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="text-[#1f2933] tabular-nums">{value}</p>
    </div>
  );
}
